import { ampFetch } from './api';

/**
 * @typedef {object}
 * @property {string} code
 * @property {string} message
 */
/**
 * @typedef {object} UserResponse
 * @property {string} id
 * @property {string} name
 * @property {string} email
 * @property {string} [stripe_customer_id]
 * @property {boolean} is_sms_verified
 * @property {string} [zip_code_billing]
 * @property {string} [zip_code_customer_provided]
 * @property {string} [referral_code]
 * @property {string} [birthdate] datetime
 * @property {string} [phone_number]
 * @property {boolean} is_receiving_promotion_texts
 * @property {boolean} is_receiving_account_texts
 * @property {boolean} is_receiving_account_emails
 * @property {boolean} is_receiving_promotion_emails
 */
/**
 * @typedef {'Standard'|'Fleet'|'Fleet Pay Per Wash'|'Employee'} AccountType
 */
/**
 * @typedef {object} AccountResponse
 * @property {string} name
 * @property {string} id
 * @property {AccountType} type
 * @property {SubscriptionBillingApproach} subscription_billing_approach
 * @property {string} user_id_primary_account_holder
 * @property {string} created_date datetime
 * @property {string} modified_date datetime
 * @property {string} [created_by_user_id]
 * @property {string} [modified_by_user_id]
 */
/**
 * @typedef UserAndAccountResponse
 * @property {UserResponse} user
 * @property {AccountResponse} account
 */
/**
 * @typedef {object} UserAuthData
 * @property {boolean} has_auth_id
 * @property {boolean} has_password
 * @property {boolean} has_amp_user
 * @property {'ARCHIVED'|'COMPROMISED'|'CONFIRMED'|'USE_TEMPORARY_PASSWORD'|'REQUEST_TEMPORARY_PASSWORD'|'UNCONFIRMED'|'UNKNOWN'} auth_status
 * @property {string} name
 */

const user = {
    /**
     * @typedef ForgotPasswordResponse
     * @property {'SUCCESS'|'INVALID_PARAMETER'|'LIMIT_EXCEEDED'|'NOT_AUTHORIZED'|'FAILURE'} status
     */
    /**
     * @param {string} email
     * @param {string} recaptchaToken
     * @returns {Promise<ForgotPasswordResponse>}
     */
    forgotPassword: async (email, recaptchaToken) => {
        return await ampFetch({
            route: 'api/auth/forgot-password',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    email,
                    'g-recaptcha-response': recaptchaToken
                })
            }
        });
    },
    /**
     * @typedef ChangePasswordResponse
     * @property {'SUCCESS'|'CODE_MISMATCH'|'EXPIRED_CODE'|'INVALID_PARAMETER'|'NOT_AUTHORIZED'|'TOO_MANY_FAILED_ATTEMPTS'|'FAILURE'} status
     */
    /**
     * @param {string} token
     * @param {string} new_password
     * @param {string} confirm_password
     * @param {string} recaptchaToken
     * @returns {Promise<*>}
     */
    confirmForgotPassword: async (
        email,
        code,
        new_password,
        confirm_password,
        recaptchaToken
    ) => {
        return await ampFetch({
            route: 'api/auth/confirm-forgot-password',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    email,
                    code,
                    new_password,
                    confirm_password,
                    'g-recaptcha-response': recaptchaToken
                })
            }
        });
    },
    /**
     * @typedef {object} SignUpUserResponse
     * @property {'SUCCESS'|'USERNAME_EXISTS'|'INVALID_PASSWORD'|'FAILURE'} status
     * @property {UserAuthData} success
     */
    /**
     * @param {string} username
     * @param {string} password
     * @param {string} recaptchaToken
     * @returns {Promise<SignUpUserResponse>}
     */
    signUp: async (username, password, recaptchaToken) => {
        return await ampFetch({
            route: 'api/auth/sign-up',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'email': username,
                    password,
                    'g-recaptcha-response': recaptchaToken
                })
            }
        });
    },
    /**
     * @typedef {object} SignInUserResponse
     * @property {string} access - JWT access token
     * @property {string} refresh - JWT refresh token
     */
    /**
     * @param {string} username
     * @param {string} password
     * @param {string} recaptchaToken
     * @returns {Promise<SignInUserResponse>}
     */
    signIn: async (username, password, recaptchaToken) => {
        return await ampFetch({
            route: 'api/auth/sign-in',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'email': username,
                    password,
                    'g-recaptcha-response': recaptchaToken
                })
            }
        });
    },
    /**
     * @typedef {object} SignOutResponse
     * @property {boolean} success
     */
    /**
     * @param {string} refreshToken
     * @returns {Promise<SignOutResponse>}
     */
    signOut: async (refreshToken) => {
        return await ampFetch({
            route: 'api/auth/sign-out',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    refresh: refreshToken
                })
            }
        });
    },
    /**
     * @typedef {object} RefreshTokenResponse
     * @property {string} access - New JWT access token
     */
    /**
     * @param {string} refreshToken
     * @returns {Promise<RefreshTokenResponse>}
     */
    refreshToken: async (refreshToken) => {
        return await ampFetch({
            route: 'api/auth/token/refresh',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    refresh: refreshToken
                })
            }
        });
    },
    /**
     * @typedef {object} ConfirmUserResponse
     * @property {'SUCCESS'|'CODE_MISMATCH'|'FAILURE'} status
     * @property {UserAuthData} success
     */
    /**
     * @param {string} username
     * @param {string} confirmation_code
     * @returns {Promise<ConfirmUserResponse>}
     */
    confirmUser: async (username, confirmation_code) => {
        return await ampFetch({
            route: 'api/auth/confirm-user',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    email: username,
                    code: confirmation_code
                })
            }
        });
    },
    /**
     * @typedef {object} ResendConfirmationCodeResponse
     * @property {'SUCCESS'|'FAILURE'} status
     */
    /**
     * @param {string} username
     * @returns {Promise<ResendConfirmationCodeResponse>}
     */
    resendConfirmationCode: async (username) => {
        return await ampFetch({
            route: 'api/auth/resend-confirmation-code',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    email: username
                })
            }
        });
    },
    /**
     * @returns {Promise<UserResponse>}
     */
    getCurrentUser: async () => {
        return await ampFetch({
            route: 'api-user/get-current-user',
            options: {
                method: 'GET'
            }
        });
    },
    delete: async ({ id }) => {
        return await ampFetch({
            route: 'api-user/user/delete',
            options: {
                method: 'POST',
                body: JSON.stringify({ id })
            }
        });
    },
    /**
     * @param id
     * @param first_name
     * @param last_name
     * @param phone
     * @param zip_code
     * @param birthday
     * @param push_notifications_enabled
     * @param email_notifications_enabled
     *
     * @returns {Promise<UserResponse>}
     */
    updateUser: async ({
        id,
        first_name,
        last_name,
        phone,
        zip_code,
        birthday,
        push_notifications_enabled,
        email_notifications_enabled
    }) => {
        return await ampFetch({
            route: 'api-user/update-user',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    id,
                    first_name,
                    last_name,
                    phone,
                    zip_code,
                    birthday,
                    push_notifications_enabled,
                    email_notifications_enabled
                })
            }
        });
    }
};

export default user;
